import React, { FC, useCallback, useContext, useEffect, useRef, useState } from 'react'
import ReCAPTCHA from 'react-google-recaptcha'
import { MessageDescriptor } from 'react-intl'
import { connect, ConnectedProps, useSelector } from 'react-redux'
import { useLocation, useNavigate } from 'react-router-dom'

import BottomSheetGorhom from '@gorhom/bottom-sheet'
import { Map } from 'immutable'
import { noop } from 'lodash-es'

import { UNAUTHENTICATED_REDIRECT_URL } from '@lyrahealth-inc/lyraweb-mobile/src/hooks/useShareSessionStorageAcrossTabs'
import { LD_CUSTOM_EVENTS } from '@lyrahealth-inc/shared-app-logic/src/features/common/constants/constants'
import { codeRedirectErrorMessage } from '@lyrahealth-inc/shared-app-logic/src/features/common/constants/messages'
import {
  REGISTRATION_EVENTS,
  REGISTRATION_PAGES,
} from '@lyrahealth-inc/shared-app-logic/src/features/common/constants/mixpanelConstants'
import { PAGE_ROUTES } from '@lyrahealth-inc/shared-app-logic/src/features/common/constants/routingConstants'
import { GOOGLE_PERFORMANCE_URL } from '@lyrahealth-inc/shared-app-logic/src/features/common/constants/urlsToHandleWithInternalNavigation'
import { sanitizeEmailAddressInput } from '@lyrahealth-inc/shared-app-logic/src/features/common/utils/stringUtils'
import {
  EmailCaptureV2Description,
  EmailCaptureV2Metric,
} from '@lyrahealth-inc/shared-app-logic/src/features/experiments/emailCaptureV2/emailCaptureExperimentV2Types'
import { useEmailCaptureExperimentV2 } from '@lyrahealth-inc/shared-app-logic/src/features/experiments/emailCaptureV2/useEmailCaptureExperimentV2'
import { ExperimentPayload } from '@lyrahealth-inc/shared-app-logic/src/features/experiments/types'
import { useLDClient } from '@lyrahealth-inc/shared-app-logic/src/features/flags/launchdarkly-client-sdk.web'
import { useLyraIntl } from '@lyrahealth-inc/shared-app-logic/src/features/intl/hooks/useLyraIntl'
import { PathwaysRedirectUrls } from '@lyrahealth-inc/shared-app-logic/src/features/pathways/constants'
import { ReCaptchaPrivacyAndTerms } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/captcha/ReCaptchaPrivacyAndTerms'
import { IS_WEB, Layout } from '@lyrahealth-inc/ui-core-crossplatform/src/constants'
import { CustomerTerminationModal } from '@lyrahealth-inc/ui-core-crossplatform/src/organisms/customerTerminationModal/CustomerTerminationModal'
import { Modal } from '@lyrahealth-inc/ui-core-crossplatform/src/organisms/modal/Modal'
import { RegistrationEmailCaptureForm } from '@lyrahealth-inc/ui-core-crossplatform/src/organisms/registrationEmailCaptureForm/RegistrationEmailCaptureForm'
import {
  EmailCaptureFormValues,
  RegistrationEmailCaptureFormV2,
} from '@lyrahealth-inc/ui-core-crossplatform/src/organisms/registrationEmailCaptureForm/RegistrationEmailCaptureFormV2'
import { RegistrationVariantEmailCaptureForm } from '@lyrahealth-inc/ui-core-crossplatform/src/organisms/registrationEmailCaptureForm/RegistrationVariantEmailCaptureForm'
import { toJS } from '@lyrahealth-inc/ui-core-crossplatform/src/utils/hocs/toJS'
import { ProductTypeContext } from '@lyrahealth-inc/ui-core-crossplatform/src/utils/productType/productTypeContext'

import styles from './registrationEmailCaptureContainer.module.scss'
import { ERROR_CODES, LOCAL_STORAGE_KEY } from '../../../common/constants/appConstants'
import { REDIRECT_URL_KEY } from '../../../common/constants/routingConstants'
import { useDefaultMixpanelEntryPoint } from '../../../common/hooks/useDefaultMixpanelEntryPoint'
import { useDefaultMixpanelPage } from '../../../common/hooks/useDefaultMixpanelPage'
import { useGetSsoIframeContent } from '../../../common/hooks/useGetSsoIframeContent'
import { useShowEmailCapturePageExperimentV2 } from '../../../common/hooks/useShowEmailCapturePageExperimentV2'
import { useTrackViewPage } from '../../../common/hooks/useTrackViewPage'
import { postGenerateOneTimePasscode } from '../../../common/http/data/verifyEmail'
import { getCaptchaKey } from '../../../common/utils/customerUtils'
import { logToSumoLogic } from '../../../common/utils/userUtils'
import { postExperimentEntrypoint } from '../../../data/appGlobals/appGlobalsActions'
import { getDeviceUUID } from '../../../data/appGlobals/appGlobalsSelectors'
import {
  getCustomerName,
  getDepartingRedirectURL,
  getHideDependentField,
  getLibraryDisabled,
} from '../../../data/customer/customerSelectors'
import { trackEventWithObj } from '../../../data/mixpanel'
import {
  getSelectedEssentialsTopicIds,
  getSelectedPathwayOption,
  isInternationalRegistrationExperience,
} from '../../../data/register/registerSelectors'
import { useAppDispatch } from '../../../data/storeConfiguration/store'
import { setShouldSaveWellnessCheckInPostLogin } from '../../wellnessCheckIn/data/wellnessCheckInActions'
import { getWellnessCheckInStateResults } from '../../wellnessCheckIn/data/wellnessCheckInSelectors'
import { dispatchMixpanelForEmailConfirmation } from '../data/registerActions'

const { UNPROCESSABLE_ENTITY: USER_ALREADY_VERIFIED_STATUS_CODE } = ERROR_CODES

type RegistrationEmailCaptureContainerProps = ConnectedProps<typeof connector> & {
  isModalView?: boolean
  showModalDelay?: number
  enableModalClose?: boolean
  onCloseRegistrationModal?: () => void
  isPreview?: boolean
  previewSubmitCallback?: (email: string, error?: string) => void
  previewComponents?: React.ReactElement
  /** Entry point to be tracked after landing in the app after registration */
  registrationEntryPoint?: string
}

const RegistrationEmailCaptureContainer: FC<RegistrationEmailCaptureContainerProps> = ({
  recaptchaKey,
  customerName,
  departingRedirectUrl,
  selectedPathwayOption,
  selectedEssentialsTopicIds,
  isModalView = false,
  showModalDelay = 0,
  enableModalClose = false,
  onCloseRegistrationModal,
  trackEventWithObj,
  isPreview = false,
  previewSubmitCallback = () => {},
  previewComponents,
  registrationEntryPoint,
}) => {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const { productType } = useContext(ProductTypeContext)
  const { activeLanguage } = useLyraIntl()
  const { showEmailCapturePageExperimentV2, emailCapturePageExperimentV2 } = useShowEmailCapturePageExperimentV2()
  const { state, pathname } = useLocation()

  const [isEmailError, setIsEmailError] = useState(Boolean(state?.isEmailError))
  const [recaptchaResponse, setRecaptchaResponse] = useState('none')
  const [isGenerateVerificationTokenLoading, setIsGenerateVerificationTokenLoading] = useState(false)
  const [emailValue, setEmailValue] = useState('')
  const [isUserGoogleCriticalTalent, setIsUserGoogleCriticalTalent] = useState(false)

  const captchaRef = useRef<HTMLFormElement>(null)
  const mixpanelEntryPoint = useDefaultMixpanelEntryPoint()
  const mixpanelPage = useDefaultMixpanelPage()
  const deviceUUID = useSelector(getDeviceUUID)

  /** Entry point to track post registration */
  const entryPointToTrackAfterRegistering = state?.registrationEntryPoint || registrationEntryPoint
  const wellnessCheckInStateResults = useSelector(getWellnessCheckInStateResults)
  const wellnessCheckInStorageStr = isPreview ? JSON.stringify(wellnessCheckInStateResults) : ''
  const isInternationalExperience = useSelector(isInternationalRegistrationExperience)

  const hideDependentField = useSelector(getHideDependentField)
  const libraryDisabled = useSelector(getLibraryDisabled)
  let emailErrorMessage: MessageDescriptor
  if (state?.isEmailError && state?.isCodeRedirectError) {
    emailErrorMessage = codeRedirectErrorMessage
  }

  const ldClient = useLDClient()
  const ldClientCallback = (event: LD_CUSTOM_EVENTS, payload: ExperimentPayload) => {
    ldClient?.track(event, payload)
  }
  const postMixpanelCallback = (event: string, eventData: { [key: string]: any }) => {
    trackEventWithObj({ event, ...eventData })
  }
  const logToSumo = (message: string) => {
    logToSumoLogic('lyraWebErrors', null, {
      message: message,
      type: 'error',
    })
  }

  const { submitExperimentPayload, arm } = useEmailCaptureExperimentV2({
    logToSumo,
    deviceUUID,
    customerName,
    productType,
    postMixpanelCallback,
    emailCapturePageExperimentV2,
    postLDCallback: ldClientCallback,
    postExperimentCallback: postExperimentEntrypoint,
  })

  const { renderGoogleSSOIframe, renderAppleSSOIframe } = useGetSsoIframeContent(
    selectedPathwayOption,
    entryPointToTrackAfterRegistering,
    submitExperimentPayload,
  )

  useEffect(() => {
    const redirectUrl = window.sessionStorage.getItem(UNAUTHENTICATED_REDIRECT_URL)
    if (redirectUrl?.includes(GOOGLE_PERFORMANCE_URL)) {
      localStorage.setItem(LOCAL_STORAGE_KEY.IS_USER_GOOGLE_CRITICAL_TALENT, 'true')
      setIsUserGoogleCriticalTalent(true)
    }
  }, [dispatch])

  useEffect(() => {
    if (ldClient != null && submitExperimentPayload != null) {
      submitExperimentPayload({
        metric: EmailCaptureV2Metric.VIEW_PAGE,
        description: EmailCaptureV2Description.VIEW_PAGE,
        launchDarklyEvent: LD_CUSTOM_EVENTS.EMAIL_CAPTURE_PAGE_VIEWED,
        // Omit mixpanelEvent arg since the page view event is tracked using useTrackViewPage below
      })
    }
  }, [submitExperimentPayload, ldClient])

  useTrackViewPage(
    REGISTRATION_PAGES.EMAIL_CAPTURE,
    {
      page: mixpanelPage?.page !== REGISTRATION_PAGES.EMAIL_CAPTURE ? mixpanelPage?.page : undefined, // For when this component is opened in a modal
      arm,
    },
    { entryPoint: mixpanelEntryPoint },
  )

  useEffect(() => {
    if (departingRedirectUrl) {
      window.location.replace(departingRedirectUrl as string)
    }
  }, [departingRedirectUrl])

  const submitForm = async (values: EmailCaptureFormValues) => {
    const sanitizedEmail = sanitizeEmailAddressInput(values.emailAddress)
    if (sanitizedEmail) {
      values.emailAddress = sanitizedEmail
    }

    setIsGenerateVerificationTokenLoading(true)
    try {
      const payload = {
        username: encodeURIComponent(values.emailAddress),
        language: encodeURIComponent(activeLanguage),
        ...(deviceUUID && { deviceUUID: encodeURIComponent(deviceUUID) }),
        ...(selectedPathwayOption && { pathwayOption: encodeURIComponent(selectedPathwayOption) }),
        ...(selectedEssentialsTopicIds && { essentialsTopicIds: encodeURIComponent(selectedEssentialsTopicIds) }),
        ...(entryPointToTrackAfterRegistering && { entryPoint: encodeURIComponent(entryPointToTrackAfterRegistering) }),
        ...(wellnessCheckInStorageStr && { wellnessCheckInBody: wellnessCheckInStorageStr }),
      }

      await postGenerateOneTimePasscode(payload)

      // TODO: Uncomment below once email capture experiment v2 is stopped.
      // LaunchDarkly flag: emailCapturePageExperimentV2
      // dispatchMixpanelForEmailConfirmation()

      submitExperimentPayload({
        metric: EmailCaptureV2Metric.CLICK_CONTINUE,
        description: EmailCaptureV2Description.CLICK_CONTINUE,
        launchDarklyEvent: LD_CUSTOM_EVENTS.EMAIL_CAPTURE_PAGE_CONTINUE,
        mixpanelEvent: REGISTRATION_EVENTS.REQUEST_EMAIL_VERIFICATION,
      })

      // Wellness check in flow should stay on current page rather than redirect
      if (isPreview && previewSubmitCallback) {
        previewSubmitCallback(values.emailAddress)
      } else {
        navigate(`/register/code?email=${encodeURIComponent(values.emailAddress)}`)
      }
    } catch (error: any) {
      if (error?.response?.status === USER_ALREADY_VERIFIED_STATUS_CODE) {
        if (isPreview && previewSubmitCallback) {
          previewSubmitCallback(values.emailAddress, 'registered')
        } else {
          navigate(`/register/verify?error=registered`)
        }
      } else {
        setIsEmailError(true)
      }
    } finally {
      setIsGenerateVerificationTokenLoading(false)
    }
  }

  const onSignUpButtonPress = async (values: EmailCaptureFormValues) => {
    setIsEmailError(false)
    if (recaptchaResponse === 'none') {
      try {
        await captchaRef?.current?.execute()
        submitForm(values)
      } catch (error: unknown) {
        console.error(error as string)
      }
    } else {
      submitForm(values)
    }
  }

  const onSignInLinkPress = () => {
    if (isPreview) {
      window.sessionStorage.setItem(REDIRECT_URL_KEY, JSON.stringify(PathwaysRedirectUrls.WELLNESS_CHECK_IN))
      dispatch(setShouldSaveWellnessCheckInPostLogin(true))
    }
    navigate(`/login`)
  }

  const handleRecaptchaSuccess = (response: string) => {
    setRecaptchaResponse(response)
  }

  const renderReCaptchaContent = () => {
    return (
      <>
        <ReCaptchaPrivacyAndTerms singleLineTerms={isModalView} />
        {/* @ts-expect-error TS(2769): No overload matches this call. */}
        <ReCAPTCHA
          className={styles.hide}
          ref={captchaRef}
          size='invisible'
          badge='inline'
          sitekey={recaptchaKey}
          onChange={handleRecaptchaSuccess} // Success Callback
        />
      </>
    )
  }

  const bottomSheetRef = useRef<BottomSheetGorhom>(null)
  const snapHeight = useRef(Layout.window.height).current - 40
  const snapPoints = [snapHeight]

  const openBottomSheet = useCallback(() => {
    bottomSheetRef.current?.expand()
  }, [])

  const onClose = useCallback(() => {
    bottomSheetRef.current?.close()
    if (onCloseRegistrationModal) {
      onCloseRegistrationModal()
    }
  }, [onCloseRegistrationModal])

  const renderFormContent = () => {
    if (isPreview) {
      return (
        //TODO: Remove RegistrationVariantEmailCaptureForm and modify RegistrationEmailCaptureForm to support isPreview mode (https://lyrahealth.atlassian.net/browse/CEA-2582)
        <RegistrationVariantEmailCaptureForm
          isEmailError={isEmailError}
          emailErrorMessage={emailErrorMessage}
          onSignInLinkPress={onSignInLinkPress}
          onSignUpButtonPress={onSignUpButtonPress}
          reCaptchaContent={renderReCaptchaContent()}
          customAppleSSOContent={renderAppleSSOIframe()}
          customGoogleSSOContent={renderGoogleSSOIframe()}
          isGenerateVerificationTokenLoading={isGenerateVerificationTokenLoading}
          isPreview={isPreview}
          previewComponents={previewComponents}
        />
      )
    }

    // This experiment is purely for the /register/connect view, no support for modal view at this time
    if (!isModalView && pathname.startsWith(PAGE_ROUTES.REGISTER) && showEmailCapturePageExperimentV2) {
      return (
        <RegistrationEmailCaptureFormV2
          isEmailError={isEmailError}
          emailErrorMessage={emailErrorMessage}
          onSignInLinkPress={onSignInLinkPress}
          onSignUpButtonPress={onSignUpButtonPress}
          reCaptchaContent={renderReCaptchaContent()}
          customAppleSSOContent={renderAppleSSOIframe()}
          customGoogleSSOContent={renderGoogleSSOIframe()}
          isGenerateVerificationTokenLoading={isGenerateVerificationTokenLoading}
          customerName={customerName}
          isInternationalRegistrationExperience={isInternationalExperience}
          hideDependentField={hideDependentField}
          libraryDisabled={libraryDisabled}
          emailValue={emailValue}
          onEmailChange={setEmailValue}
          isUserGoogleCriticalTalent={isUserGoogleCriticalTalent}
        />
      )
    } else {
      // Old Form 'Control' group of experiment
      return (
        <RegistrationEmailCaptureForm
          isModalView={isModalView}
          isEmailError={isEmailError}
          emailErrorMessage={emailErrorMessage}
          onSignInLinkPress={onSignInLinkPress}
          onSignUpButtonPress={onSignUpButtonPress}
          reCaptchaContent={renderReCaptchaContent()}
          customAppleSSOContent={renderAppleSSOIframe()}
          customGoogleSSOContent={renderGoogleSSOIframe()}
          isGenerateVerificationTokenLoading={isGenerateVerificationTokenLoading}
        />
      )
    }
  }

  return (
    <>
      <CustomerTerminationModal customerName={customerName} />
      {isModalView ? (
        <Modal
          modalContents={renderFormContent()}
          visible
          scrollable
          scrollableModalWidth='464px'
          scrollableModalHeight='auto'
          scrollableModalMaxHeight={IS_WEB ? '90vh' : undefined}
          showCloseIcon={enableModalClose}
          showDelay={showModalDelay}
          onRequestClose={enableModalClose ? onClose : noop}
          onCloseEnd={enableModalClose ? onClose : noop}
          snapPoints={snapPoints}
          disableClose={!enableModalClose}
          onLayout={openBottomSheet}
          showCloseIconInBottomSheet={enableModalClose}
        />
      ) : (
        <div className={styles.rootContainer}>{renderFormContent()}</div>
      )}
    </>
  )
}

const mapStateToProps = ($$state: Map<string, any>) => {
  return {
    recaptchaKey: getCaptchaKey($$state),
    customerName: getCustomerName($$state),
    departingRedirectUrl: getDepartingRedirectURL($$state),
    selectedPathwayOption: getSelectedPathwayOption($$state),
    selectedEssentialsTopicIds: getSelectedEssentialsTopicIds($$state),
  }
}

const mapDispatchToProps = {
  dispatchMixpanelForEmailConfirmation,
  trackEventWithObj,
}

const connector = connect(mapStateToProps, mapDispatchToProps)

export default connector(toJS(RegistrationEmailCaptureContainer))
